import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useJoinAdhocMeetingMutation } from '../../store/media/media'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { getSupportedProviderFromAdhocMeetingLink, PROVIDER_NAME_MAPPING, SUPPORTED_MEETING_PROVIDERS } from '../../common/meetingProviders'
import { useSessionAvailability } from '../ScopedBlock/hooks'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { NotificationRequestModal } from './NotificationRequestModal'
import { AccountAwareIntegrationIcon } from '../AccountAwareIntegrationIcon'
import { FormFeedback } from '../Form/FormFeedback'
import { ScopedBlock } from '../ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../ScopedBlock/models'
import { Button } from '../Button/Button'
import { IntegrationServiceId } from '../../common/integrations'
import { IntegrationRequirementMessage } from '../../common/IntegrationRequirementMessage'
import { useIntegrationRequirement } from '../../common/useIntegrationRequirement'
import { useGetMeQuery } from '../../store/user/user'
import { useMultiLanguageEnabled } from '../../common/useMultiLanguageEnabled'
import { ZoomOBFWarningRow } from '../ZoomOBFWarning/ZoomOBFWarningRow'

type AdhocToolFormValues = {
  meetingLink: string
}

export const AdhocTool = () => {
  const accountId = useAppSelector(selectAccountId)
  const [showNotificationRequestModal, setShowNotificationRequestModal] = useState(false)
  const [showComingSoon, setShowComingSoon] = useState(false)

  const handleSaveNotification = () => {
    setShowNotificationRequestModal(false)
  }

  const handleHideNotificationModal = () => {
    setShowNotificationRequestModal(false)
    setShowComingSoon(false)
  }

  const defaultValues = {
    meetingLink: '',
  }
  const { control, clearErrors, handleSubmit, watch, reset } = useForm<AdhocToolFormValues>({ defaultValues, reValidateMode: 'onSubmit' })
  const { isFetching: isMeFetching } = useGetMeQuery({ accountId })
  const checkRequirement = useIntegrationRequirement()
  const [joinSuccess, setJoinSuccess] = useState(false)
  const [joinAdhocMeeting, { isLoading }] = useJoinAdhocMeetingMutation()
  const watchMeetingLink = watch('meetingLink')
  const { tracked } = useTracking()
  const multiLanguageEnabled = useMultiLanguageEnabled()

  const notifyHandler = tracked(
    (service: string) => {
      const providerName = PROVIDER_NAME_MAPPING[service]
      window.localStorage.setItem(`${providerName}_notification`, 'enabled')
      setShowNotificationRequestModal(true)
    },
    {
      eventName: TrackEvent.NOTIFICATION_REQUESTED,
    },
    {
      eventDetailsExtractor: (service: string) => ({ featureName: service }),
    },
  )

  const onSubmit = async (data: AdhocToolFormValues) => {
    const meetingProvider = getSupportedProviderFromAdhocMeetingLink(data.meetingLink)
    if (!meetingProvider) {
      return
    }
    if (SUPPORTED_MEETING_PROVIDERS.includes(meetingProvider)) {
      await joinAdhocMeeting({ accountId, body: { url: data.meetingLink } })
      setJoinSuccess(true)
      setTimeout(() => setJoinSuccess(false), 3000)
      reset()
    } else {
      setShowComingSoon(true)
    }
  }
  const { available: sessionAvailable, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()

  const atRoomSessionLimitActions = () => {
    return sessionAvailabilitySuccess && sessionAvailable
  }

  const provider = getSupportedProviderFromAdhocMeetingLink(watchMeetingLink)

  return (
    <Container className='d-flex flex-column gap-4 justify-content-between h-100 p-0'>
      <NotificationRequestModal
        show={showNotificationRequestModal}
        onHide={handleHideNotificationModal}
        onSave={handleSaveNotification}
      ></NotificationRequestModal>
      <Row>
        <Col xs='auto' className='gx-3'>
          <div className='flex rounded integration-icon-wrapper'>
            <i className='bi bi-broadcast '></i>
          </div>
        </Col>
        <Col>
          <h5>Join live meeting</h5>
          <span className='paragraph-medium zocks-text-gray-600'>Add Zocks to live meeting using a conference link.</span>
          {multiLanguageEnabled ? <span className='paragraph-medium zocks-text-gray-600'>Your meeting will be processed in English.</span> : null}
        </Col>
      </Row>
      <ZoomOBFWarningRow />
      <Row>
        <Container className='upcoming-integration-bottom-box'>
          <Row className='align-items-center'>
            <Col>
              <div className='paragraph-small zocks-text-gray-600'>It works on</div>
            </Col>

            <Col xs='auto' className='flex align-items-center justify-content-end'>
              <AccountAwareIntegrationIcon serviceId={IntegrationServiceId.ZOOM} width='2rem' height='2rem' className='me-1' />
              <AccountAwareIntegrationIcon serviceId={IntegrationServiceId.TEAMS} width='2rem' height='2rem' className='me-1' />
              <AccountAwareIntegrationIcon serviceId={IntegrationServiceId.MEET} width='2rem' height='2rem' className='me-1' />
              <AccountAwareIntegrationIcon serviceId={IntegrationServiceId.WEBEX} width='2rem' height='2rem' className='me-1' />
            </Col>
          </Row>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row className='mb-3'>
              <Col>
                <Controller
                  name='meetingLink'
                  control={control}
                  rules={{
                    validate: {
                      provider: (link) => {
                        return getSupportedProviderFromAdhocMeetingLink(link) != null || 'Invalid meeting provider'
                      },
                      // Returns `false` (no message) on failure — the render function
                      // detects `error.type === 'requirement'` and shows a custom JSX message instead.
                      requirement: (link) => {
                        const provider = getSupportedProviderFromAdhocMeetingLink(link)
                        if (!provider || !SUPPORTED_MEETING_PROVIDERS.includes(provider)) {
                          return true
                        }
                        return checkRequirement(provider as IntegrationServiceId).satisfied || false
                      },
                    },
                  }}
                  render={({ field, fieldState }) => {
                    const isRequirementError = fieldState.error?.type === 'requirement'
                    return (
                      <>
                        <Form.Control
                          id={field.name}
                          type='text'
                          placeholder='Paste conference link'
                          {...field}
                          onChange={(e) => {
                            field.onChange(e)
                            if (fieldState.error) clearErrors('meetingLink')
                          }}
                          isInvalid={!!fieldState.error}
                        />
                        {fieldState.error &&
                          (isRequirementError ? (
                            <FormFeedback>
                              <IntegrationRequirementMessage serviceId={getSupportedProviderFromAdhocMeetingLink(field.value) as IntegrationServiceId} />
                            </FormFeedback>
                          ) : (
                            <FormFeedback>{fieldState.error.message}</FormFeedback>
                          ))}
                      </>
                    )
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                {!showComingSoon ? (
                  <ScopedBlock
                    scope={{
                      scope: 'room:create',
                      superScope: ['room:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <ScopedBlock authActions={[atRoomSessionLimitActions]} missingScopeAction={MissingScopeAction.DISABLE}>
                      {joinSuccess ? (
                        <Button variant='primaryGhost' disabled={true} type='submit' loading={isLoading} className='w-100 px-0'>
                          <div className='d-flex justify-content-center align-items-center gap-2 join-success-wrapper'>
                            <i className='bi bi-check-circle' /> Joining
                          </div>
                        </Button>
                      ) : (
                        <Button variant='primary' type='submit' loading={isLoading} className='w-100 px-0' disabled={!watchMeetingLink || isMeFetching}>
                          Join
                        </Button>
                      )}
                    </ScopedBlock>
                  </ScopedBlock>
                ) : (
                  <Col>
                    <Container className='p-0'>
                      <Row className='gx-3 align-items-center'>
                        {provider ? (
                          <Col>
                            <Button onClick={() => notifyHandler(provider)} variant='link' className='text-nowrap'>
                              <span className='text-decoration-none paragraph-small'>
                                <i className='bi bi-bell' />
                              </span>
                              <div className='paragraph-small'>Please notify me</div>
                            </Button>
                          </Col>
                        ) : null}
                        <Col>
                          <Button disabled variant='primary' className='paragraph-small text-nowrap'>
                            Coming soon
                          </Button>
                        </Col>
                      </Row>
                    </Container>
                  </Col>
                )}
              </Col>
            </Row>
          </form>
        </Container>
      </Row>
    </Container>
  )
}
